<template>
  <section class="home-block py-0 text-white">
    <div class="background">
      <div ref="background" class="video-background">
        <video
          v-if="content.video"
          autoplay
          muted
          loop
          playsinline
          :src="content.video"
        />
        <picture v-else-if="content.images">
          <source
            v-for="(image, idx) in content.images.srcSet"
            :key="idx"
            :type="image?.type"
            :srcset="image.srcset"
            :media="image.media"
          />
          <img
            :src="content.images.default"
            :alt="content.images.alt"
            loading="lazy"
          />
        </picture>
      </div>
    </div>
    <div ref="particle" class="particle" />
    <div class="content">
      <div ref="content" class="container">
        <PlayCamera v-if="allowPlayCamera" />
        <slot name="top" />
        <div v-if="label" class="label">
          <div v-if="labelLink" class="h5">
            <router-link
              class="stretched-link"
              :to="{ name: labelLink }"
              v-html="label"
            />
          </div>
          <div v-else class="h5" v-html="label" />
        </div>
        <Breadcrumbs v-if="breadcrumbs" :links="breadcrumbs" />
        <component
          :is="titleTag"
          v-if="!content.link"
          class="title"
          :class="{ h1: titleTag == 'div' }"
          v-html="content.title"
        ></component>
        <component
          :is="titleTag"
          v-else
          class="title"
          :class="{ h1: titleTag == 'div' }"
          ><router-link
            :to="{ name: content.link }"
            v-html="content.title"
          ></router-link
        ></component>
        <div
          v-if="content.subtitle"
          class="subtitle"
          v-html="content.subtitle"
        />
        <div v-if="content.text" class="text" v-html="content.text" />
        <slot name="btns" />
        <slot name="bottom" />
        <div v-if="content.more" class="more">
          <a href="#" @click.prevent="scrollDown" v-html="content.more" />
        </div>
        <slot name="bottom-right" />
      </div>
    </div>
  </section>
</template>

<script>
import PlayCamera from "@/components/PlayCamera.vue";
import Breadcrumbs from "@/components/UI/Breadcrumbs.vue";
import { lazyVideo, is_touch } from "@/helpers.js";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
gsap.registerPlugin(ScrollToPlugin);
export default {
  components: {
    PlayCamera,
    Breadcrumbs,
  },
  props: {
    content: {
      type: Object,
    },
    label: {
      type: String,
      required: false,
    },
    link: {
      type: String,
      required: false,
    },
    labelLink: {
      type: String,
      required: false,
    },
    breadcrumbs: {
      type: Array,
      required: false,
    },
    titleTag: {
      type: String,
      default: "h1",
    },
  },
  computed: {
    allowPlayCamera() {
      return this.$route.name == "Home";
    },
  },
  mounted() {
    if (!is_touch()) {
      gsap.to(this.$refs.background, {
        scale: 1.2,
        scrollTrigger: {
          trigger: this.$el,
          scrub: 0.5,
          start: "top top",
          end: "bottom top",
        },
      });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: this.$el,
            end: "90% top",
            toggleActions: "play reverse play reverse",
          },
        })
        .from(this.$refs.particle, {
          autoAlpha: 0,
        })
        .from(
          this.$refs.content.children,
          {
            autoAlpha: 0,
            y: 20,
            stagger: { each: 0.1 },
            clearProps: "all",
          },
          0.2
        );
    }

    // if (!deferthings) {
    //   window.addEventListener("load", () => {
    //     setTimeout(() => {
    //       lazyVideo(this.$el);
    //     }, 3000);
    //   });
    // } else {
    //   lazyVideo(this.$el);
    // }
  },
  methods: {
    scrollDown() {
      gsap.to(window, {
        scrollTo: this.$el.clientHeight,
        duration: "1",
        ease: "power2.inOut",
      });
    },
  },
};
</script>

<style scoped>
.home-block {
  background: #291f1f33;
  text-align: center;
}

.background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.background::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgb(41 31 31 / 0.2);
}

.particle {
  position: absolute;
  top: -50px;
  left: 0;
  width: 100%;
  height: calc(100% + 70px);
  background: url(~@i/logo-symbol.svg) 50% 90px / auto calc(100% + 50px)
    no-repeat;
}

.content {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 760px;
  padding: 110px 0;
}

.h1 {
  margin-bottom: 20px;
}

:deep(.content .play-camera) {
  --size: 178px;
  position: absolute;
  right: 20px;
  bottom: 20px;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
}

:deep(.content .play-camera__words) {
  filter: brightness(0) invert(1);
}

.label {
  position: relative;
  max-width: 500px;
  margin-right: auto;
  margin-bottom: 45px;
  margin-left: auto;
}

.label h5,
.label .h5 {
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 7px 16px 9px;
  border-radius: 20px;
  background: #509c3b;
  font-weight: bold;
}

.label h5 a,
.label .h5 a {
  color: inherit;
  text-decoration: none;
}

body.-notouch .label h5 a:hover,
body.-notouch .label .h5 a:hover {
  text-decoration: underline;
}

.title {
  max-width: 1030px;
  margin-right: auto;
  margin-left: auto;
}

.title a {
  text-decoration: none;
}

body.-notouch .title a:hover {
  text-decoration: underline;
  text-decoration-thickness: 5px;
}

.subtitle {
  font-weight: 500;
  font-size: 72px;
}

.text {
  max-width: 800px;
  margin: 0 auto;
  font-size: 20px;
}

.btns-list {
  margin-top: 57px;
}

.more {
  position: absolute;
  bottom: 35px;
  left: 0;
  z-index: 1;
  width: 100%;
  font-weight: 500;
  font-size: 16px;
  line-height: 130%;
  text-align: center;
}

.more a {
  text-decoration: none;
  opacity: 0.8;
}

body.-notouch .more a:hover {
  opacity: 1;
}

@media (max-width: 1399px) {
  :deep(.content .play-camera) {
    right: 0;
    bottom: 0;
  }
}

@media (max-width: 992px) {
  .title {
    margin-top: 110px;
  }
}

@media (max-width: 767px) {
  .content {
    height: 810px;
    min-height: 500px;
    max-height: 100vh;
  }

  .subtitle {
    font-size: 36px;
  }

  .more {
    position: static;
    margin-top: 12px;
  }

  :deep(.content .play-camera) {
    --size: 90px;

    top: 135px;
    right: 0;
  }

  .text {
    font-size: 16px;
  }

  .label {
    margin-bottom: 27px;
  }

  .label h5,
  .label .h5 {
    padding: 8px 16px;
    font-size: 12px;
  }

  .title {
    margin-top: 211px;
    margin-bottom: 12px;
  }

  :slotted(.btns-list__item) {
    margin: 0 0 10px;
  }

  :slotted(.btns-list__item:last-child) {
    margin-bottom: 0;
  }

  .particle {
    background-size: 770px auto;
  }
}
@media (max-width: 575px) {
  :slotted(.btns-list .btn-transparent) {
    padding-right: 10px;
    padding-left: 10px;
  }
}

.breadcrumbs {
  justify-content: center;
  margin-bottom: 40px;
}
@media (max-width: 767px) {
  .breadcrumbs {
    margin-bottom: 30px;
    font-size: 16px;
  }
}
</style>
